// Utilities to scope URLs to the current practice slug for Builder V2

// Check if we're currently on a custom domain
export const isCustomDomain = () => {
  if (typeof window === "undefined") return false

  const hostname = window.location.hostname
  // Custom domains are typically not localhost, 127.0.0.1, or the main heal.me domain
  const isLocalhost = hostname === "localhost" || hostname === "127.0.0.1"
  const isMainDomain = hostname.includes("heal.me") || hostname.includes("healme.com")

  return !isLocalhost && !isMainDomain
}

export const getPracticeSlug = (practice) => {
  // Prefer explicit practice prop, but only if it's a vanity slug (not numeric ID)
  const fromProp = practice?.user?.vanity_slug || practice?.user?.vanitySlug || practice?.user?.slug
  if (fromProp && !/^\d+$/.test(String(fromProp))) {
    return String(fromProp)
  }

  // Fallback to current path (public path is /:practiceSlug/:pageSlug)
  if (typeof window !== "undefined") {
    const path = window.location.pathname
    const parts = path.split("/").filter(Boolean)

    if (parts.length >= 1 && parts[0] !== "portal") {
      // Handle service URLs: /{practiceSlug}/services/{serviceId}
      const serviceMatch = path.match(/^\/([^/]+)\/services\/\d+/)
      if (serviceMatch) {
        return serviceMatch[1]
      }

      // Handle other URLs: /{practiceSlug}/{pageSlug}
      return parts[0]
    }
  }

  return ""
}

// Helper function to detect if we're on a v2 site
export const isV2Site = (practice) => {
  // CRITICAL: This file is ONLY used by WebsiteBuilderV2 components
  // V1 sites use completely different navigation and URL handling

  // Primary check: practice must have v2 enabled
  if (practice?.v2 === true) return true

  // Secondary checks for edge cases
  if (typeof window !== "undefined") {
    // Check if we're on a custom domain (v2 sites can use custom domains)
    const isCustomDomain =
      !window.location.hostname.includes("heal.me") &&
      !window.location.hostname.includes("healme.com") &&
      window.location.hostname !== "localhost" &&
      window.location.hostname !== "127.0.0.1"

    if (isCustomDomain) return true

    // Check if we're on a v2 page by looking at the URL structure
    const path = window.location.pathname
    // V2 sites use the pattern /{practice_slug}/{page_slug} for public domains
    const isV2Pattern = path.match(/^\/[^/]+\/[^/]+$/) // Matches /slug/page pattern

    // Additional check: make sure we're not on legacy routes
    // Only check the first path segment after the domain
    const pathSegments = path.split("/").filter(Boolean)
    const firstSegment = pathSegments[0] || ""

    // Special handling for /review route
    // /review can be either legacy (testimonials) or v2 (reviews page)
    // We need to distinguish based on context
    if (firstSegment === "review") {
      // If we're on a custom domain, /review is likely a v2 page
      if (isCustomDomain) {
        return true // This is a v2 site
      }
      // For public domains, /review without a practice slug is legacy
      // But /{practice_slug}/review is v2
      if (pathSegments.length === 1) {
        return false // Legacy testimonials route
      }
      // If it's /{practice_slug}/review, it's v2
      return true
    }

    const isLegacyRoute =
      // Admin routes
      firstSegment === "admin" ||
      // Portal routes
      firstSegment === "portal" ||
      // Public static pages
      firstSegment === "pro" ||
      firstSegment === "pro2" ||
      firstSegment === "pro3" ||
      firstSegment === "contact" ||
      firstSegment === "wellness" ||
      firstSegment === "specialties" ||
      firstSegment === "questions-az" ||
      firstSegment === "practitioners-az" ||
      firstSegment === "about" ||
      firstSegment === "privacy" ||
      firstSegment === "terms" ||
      firstSegment === "robots" ||
      firstSegment === "demo_confirmed" ||
      firstSegment === "application_completed" ||
      firstSegment === "download_app" ||
      firstSegment === "lifer_checkout" ||
      firstSegment === "business_associate_agreement" ||
      firstSegment === "proplatform" ||
      firstSegment === "book_demo" ||
      firstSegment === "home" ||
      // Instant actions
      firstSegment === "i" || // instant reviews/appointments
      firstSegment === "r" || // review requests
      firstSegment === "c" || // confirm reviews
      firstSegment === "m" || // manage appointments
      // API routes
      firstSegment === "api" ||
      firstSegment === "webhooks" ||
      // System routes
      firstSegment === "rails" ||
      firstSegment === "assets" ||
      firstSegment === "packs" ||
      firstSegment === "graphiql" ||
      firstSegment === "graphql" ||
      // Content routes
      firstSegment === "articles" ||
      firstSegment === "questions" ||
      firstSegment === "quizzes" ||
      firstSegment === "quiz_results" ||
      firstSegment === "answers" ||
      // Booking/appointment routes
      firstSegment === "appointments" ||
      firstSegment === "my-reviews" ||
      firstSegment === "payment_methods" ||
      firstSegment === "checkout" ||
      firstSegment === "practitioner-search" ||
      firstSegment === "search" ||
      // Session/auth routes
      firstSegment === "sessions" ||
      firstSegment === "password_resets" ||
      firstSegment === "login" ||
      firstSegment === "logout" ||
      firstSegment === "settings" ||
      firstSegment === "photo" ||
      // Form routes
      firstSegment === "contacts" ||
      firstSegment === "testimonials" ||
      firstSegment === "intake_forms" ||
      // Migration routes
      firstSegment === "migrations" ||
      // Builder v2 admin routes (not public pages)
      firstSegment === "builder_pages" ||
      firstSegment === "builder_sections" ||
      firstSegment === "builder_themes" ||
      // Credential routes
      firstSegment === "credentials_requests" ||
      firstSegment === "issuers_requests" ||
      // Cronofy integration
      firstSegment === "cronofy" ||
      // Webflow routes
      firstSegment === "lp" ||
      // Confirm route
      firstSegment === "confirm" ||
      // Apply redirect
      firstSegment === "apply" ||
      // Sitemap
      firstSegment === "sitemaps" ||
      // Video upload routes
      firstSegment === "video-review" ||
      // Intercom
      firstSegment === "intercom"

    return isV2Pattern && !isLegacyRoute
  }

  // If practice object doesn't have v2 flag, don't apply the fix
  // This prevents any accidental application to v1 sites
  return false
}

// Helper function to get practice slug from current URL when practice object is not available
export const getPracticeSlugFromUrl = () => {
  if (typeof window === "undefined") return ""

  const path = window.location.pathname
  const parts = path.split("/").filter(Boolean)

  if (parts.length >= 1 && parts[0] !== "portal") {
    // Handle service URLs: /{practiceSlug}/services/{serviceId}
    const serviceMatch = path.match(/^\/([^/]+)\/services\/\d+/)
    if (serviceMatch) {
      return serviceMatch[1]
    }

    // Handle other URLs: /{practiceSlug}/{pageSlug}
    return parts[0]
  }

  return ""
}

export const scopePath = (path, practice) => {
  if (!path) return "/"
  // Leave external links untouched
  if (/^https?:\/\//i.test(path)) return path

  // If we're on a custom domain, don't add the practice slug
  if (isCustomDomain()) {
    const cleanPath = path.startsWith("/") ? path : `/${path}`
    return cleanPath
  }

  // For public domains, add the practice slug
  const slug = getPracticeSlug(practice)
  const cleanPath = path.startsWith("/") ? path : `/${path}`

  if (!slug) {
    // Only apply the fallback fix for v2 sites
    if (isV2Site(practice)) {
      // If we can't get the practice slug, try to get it from the current URL
      const currentSlug = getPracticeSlugFromUrl()
      if (currentSlug) {
        // Avoid double prefix if already scoped
        if (cleanPath.startsWith(`/${currentSlug}/`) || cleanPath === `/${currentSlug}`) return cleanPath
        return `/${currentSlug}${cleanPath}`
      }
      // Debug: Log when we can't get a practice slug for v2 sites
      console.warn("scopePath (v2): Could not determine practice slug for path:", path, "practice:", practice) // eslint-disable-line no-console
    }
    return cleanPath
  }
  // Avoid double prefix if already scoped
  if (cleanPath.startsWith(`/${slug}/`) || cleanPath === `/${slug}`) return cleanPath
  return `/${slug}${cleanPath}`
}

export const buildPublicPageUrl = (pageSlug, practice) => scopePath(`/${pageSlug}`, practice)
export const buildServiceUrl = (serviceId, practice, options = {}) => {
  // options: { query: string, anchor: string }
  let path = serviceId ? `/services/${serviceId}` : `/services`
  if (options.query) {
    path += path.includes("?") ? `&${options.query}` : `?${options.query}`
  }
  if (options.anchor) {
    path += `#${options.anchor}`
  }
  return scopePath(path, practice)
}
export const buildEventTypeUrl = (eventTypeSlug, practice) => scopePath(`/events/${eventTypeSlug}`, practice)
export const buildBookingUrl = (practice) => scopePath(`/services`, practice)
